import {Card} from 'platform/components';
import {Box, getValueByDevice, Stack, ThemeColorPath, useDevice} from 'platform/foundation';

import {ReactNode} from 'react';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {SalesLeagueKpiColumn} from './SalesLeagueKpiColumn';

const COLUMN_SPACING = 4;
const COLUMN_FLEX = 1;
const DIVIDER_BORDER = '1px solid';
const DIVIDER_COLOR: ThemeColorPath = 'general.separator';
const FIRST_COLUMN_INDEX = 0;

export interface SalesLeagueKpiItem {
  id: string;
  accentColor: ThemeColorPath;
  label: string;
  value: string | Nullish;
  unit?: string;
  footer: string;
  changeChip?: ReactNode;
}

interface SalesLeagueKpiPanelProps extends RequiredTestIdProps {
  /** Three or four measures, already gated for what this viewer may see. */
  items: SalesLeagueKpiItem[];
}

/**
 * One card holding every headline figure, divided into columns — the measures belong to a
 * single board and read as one row, not as a set of independent cards. Below the widths
 * that fit them side by side the columns stack and the dividers turn horizontal.
 */
export function SalesLeagueKpiPanel(props: SalesLeagueKpiPanelProps) {
  const device = useDevice();
  const isRow = getValueByDevice(device, false, false, true);

  return (
    <Card data-testid={suffixTestId('panel', props)}>
      <Stack direction={isRow ? 'row' : 'column'} spacing={COLUMN_SPACING} align="stretch">
        {props.items.map((item, index) => {
          const isDivided = index !== FIRST_COLUMN_INDEX;

          return (
            <Box
              key={item.id}
              flex={isRow ? COLUMN_FLEX : undefined}
              paddingLeft={isDivided && isRow ? COLUMN_SPACING : 0}
              paddingTop={isDivided && !isRow ? COLUMN_SPACING : 0}
              borderLeft={isDivided && isRow ? DIVIDER_BORDER : undefined}
              borderTop={isDivided && !isRow ? DIVIDER_BORDER : undefined}
              borderColor={DIVIDER_COLOR}
            >
              <SalesLeagueKpiColumn
                accentColor={item.accentColor}
                label={item.label}
                value={item.value}
                unit={item.unit}
                footer={item.footer}
                changeChip={item.changeChip}
                data-testid={suffixTestId(item.id, props)}
              />
            </Box>
          );
        })}
      </Stack>
    </Card>
  );
}
